import { BrandConfig, DEFAULT_CURRENCY, Locale } from '@headless-workspace/config';
import { ArrayHelpers, ValueHelpers } from '@headless-workspace/utils';
import { FlagValue, mapFlags, mapMedia, MediaValue, ProductType } from '../../../client';
import {
    CapacityFormat,
    LoyaltyConfigsValue,
    mapProductBrandData,
    mapProductPriceData,
    PaymentMethodsValue,
    PriceDTO,
    ProductBrandData,
    ProductPriceData,
    PromotionType,
    VipBrandsValue,
} from '../../../server';
import { ProductDetailDTO } from './ProductDetailDTO';
import { mapProductDetailSetItem, ProductSetItemValue } from './ProductDetailSetValue';
import { extractVariantFormats, mapProductDetailVariant, ProductDetailVariantValue } from './ProductDetailVariantValue';
import { mapProductInfo, ProductInfoTabParamsValue } from './ProductInfoTabParamsValue';
import { mapProductLoyalty, ProductLoyaltyInfoValue } from './ProductLoyaltyInfoValue';
import { mapProductDetailBanners, ProductDetailBannersValue } from './banner';
import { BreadcrumbValue, mapBreadcrumbValue } from './breadcrumb';

type BaseProductDetailValue = ProductPriceData &
    ProductBrandData & {
        id: string;
        sku: string;
        name?: {
            full: string;
            partOne: string;
            partTwo?: string;
        };
        description: string;
        vat?: number;
        rating: number;
        totalIndividualPrice?: number;
        reviewCount: number;
        stockLevel: number;
        orderable?: boolean;
        medias: MediaValue[];
        flags?: FlagValue[];
        breadcrumb: BreadcrumbValue;
        productInfoTabParams: ProductInfoTabParamsValue[];
        pricePerUnit?: string;
        socialProof?: string;
        buyNowPayLaterContent: PaymentMethodsValue;
        promotion?: PromotionType;
        banners?: ProductDetailBannersValue;
        isRedDot: boolean;
        url?: string;
        masterProductId?: string;
        hasAudio?: boolean;
        isEngravable: boolean;
        variants: ProductDetailVariantValue[];
        defaultVariantId?: string;
        formats: CapacityFormat[];
        isPriceLegalMentionEnabled: boolean;
        range?: string;
        axis?: string;
        nature?: string;
        loyalty?: ProductLoyaltyInfoValue;
    };

export type ProductDetailValue =
    | ({
          type: ProductType.PRODUCT_SET;
          productsSetList: ProductSetItemValue[]; // Mandatory when type is PRODUCT_SET
      } & BaseProductDetailValue)
    | ({
          type: Exclude<ProductType, ProductType.PRODUCT_SET>;
          productsSetList?: never;
      } & BaseProductDetailValue);

const isProductNameOnTwoLines = (brandId?: string): boolean => {
    if (!brandId) {
        return false;
    }

    return BrandConfig.productNameOnTwoLines.includes(brandId);
};

export const getPromotionKey = (promotion: ProductDetailDTO['promotion'], prices: PriceDTO) => {
    if (promotion) {
        return PromotionType[promotion];
    }
    if (!prices.originalPrice) {
        return undefined;
    }
    return prices.price !== prices.originalPrice ? PromotionType.OTHER : undefined;
};

export const mapProductDetail = (
    dto: ProductDetailDTO,
    vipBrands: VipBrandsValue,
    paymentMethods: PaymentMethodsValue,
    isPriceLegalMentionEnabled: boolean,
    locale: Locale,
    loyaltyConfig?: LoyaltyConfigsValue,
): ProductDetailValue => {
    const [namePartOne, ...namePartTwo] = dto.name
        ? isProductNameOnTwoLines(dto.brand.id)
            ? dto.name.split(' - ')
            : [dto.name]
        : [];

    const currency = dto.currency ?? DEFAULT_CURRENCY;
    const prices = mapProductPriceData(dto, currency);
    let variants: ProductDetailVariantValue[] = [];

    if (dto.type !== ProductType.PRODUCT_SET) {
        variants = dto.variants.map((variant) => mapProductDetailVariant(variant, currency));
    }

    const product = {
        id: dto.id,
        sku: dto.id,
        name: dto.name
            ? {
                  full: dto.name,
                  partOne: namePartOne,
                  partTwo: ArrayHelpers.isNotEmpty(namePartTwo) ? namePartTwo.join(' - ') : undefined,
              }
            : undefined,
        productInfoTabParams: mapProductInfo(dto),
        description: dto.description ?? '',
        vat: ValueHelpers.mapOptional(dto.vat),
        totalIndividualPrice: dto.valuePrice ?? undefined,
        rating: dto.rating ?? 0,
        reviewCount: dto.reviewCount ?? 0,
        stockLevel: dto.stockLevel ?? 0,
        orderable: ValueHelpers.mapOptional(dto.orderable),
        medias: dto.medias.map(mapMedia),
        flags: mapFlags(dto.flags),
        ...mapProductBrandData(dto, vipBrands),
        ...prices,
        breadcrumb: mapBreadcrumbValue(dto.breadcrumbs),
        pricePerUnit: ValueHelpers.mapOptional(dto.pricePerUnit),
        socialProof:
            vipBrands[dto.brand.id ?? '']?.isNotSocialProof === true
                ? undefined
                : ValueHelpers.mapOptional(dto.socialProof),
        buyNowPayLaterContent: paymentMethods,
        promotion: getPromotionKey(dto.promotion, prices),
        type: dto.type ? ProductType[dto.type] : ProductType.MONO_SKU,
        banners: mapProductDetailBanners(dto.banners), // FIXME: to be adjusted when unmock
        isRedDot: dto.isRedDot ?? false,
        hasAudio: dto.hasAudio,
        masterProductId: ValueHelpers.mapOptional(dto.masterId),
        isEngravable: dto.isEngravable,
        isPriceLegalMentionEnabled: isPriceLegalMentionEnabled,
        formats: extractVariantFormats(variants),
        defaultVariantId: ValueHelpers.mapOptional(dto.defaultVariantId),
        loyalty: mapProductLoyalty(ValueHelpers.mapOptional(dto.loyaltyPointsToEarn), locale, loyaltyConfig),
        variants,
        axis: ValueHelpers.mapOptional(dto.axis),
        range: ValueHelpers.mapOptional(dto.range),
        nature: ValueHelpers.mapOptional(dto.nature),
    };

    if (dto.type === ProductType.PRODUCT_SET) {
        return {
            ...product,
            type: ProductType.PRODUCT_SET,
            productsSetList: dto.products.map(mapProductDetailSetItem(currency)),
        };
    }

    return {
        ...product,
        sku: dto.defaultVariantId ?? dto.id,
        type: dto.type ? ProductType[dto.type] : ProductType.MONO_SKU,
    };
};
